import React from "react";
import Input from "../../../controls/Input";
import SelectMenu from "../../../controls/SelectMenu";
import { InputNumber } from "primereact/inputnumber";
import { useForm } from "../../useForm";
import { yesOrNoOptions } from "../../../../shared/utility";
import { makePostApiCall } from "../../../../shared/makeApiCall";
import ProgressModal from "../../../modals/ProgressModal";

const qualityConditionOptions = [
  { value: 0, label: "N/A" },
  { value: 1, label: "Satisfactory" },
  { value: 2, label: "NOT Satisfactory" },
];

function findConditionOption(value) {
  return (
    qualityConditionOptions.find((c) => c.value === value) || {
      value: 1,
      label: "Satisfactory",
    }
  );
}

function findYesNoOption(value) {
  const raw =
    value != null && typeof value === "object" && "value" in value
      ? value.value
      : value;
  const n = Number(raw);
  const key = Number.isFinite(n) ? n : raw;
  return (
    yesOrNoOptions.find((o) => o.value === key) ?? {
      label: "N/A",
      value: 0,
    }
  );
}

export default function EditProductSlideOver(props) {
  const {
    product,
    invoiceId,
    setSlideOver,
    setInvoiceItems,
    setEmptyModelOpen,
    showErrorModal,
    onSaveSuccess,
  } = props;

  const initialFValues = {
    unit_cost: product?.unit_cost ?? null,
    total_cost: product?.total_cost ?? null,
    product_temperature: product?.product_temperature ?? "",
    product_condition:
      product?.product_condition != null
        ? findConditionOption(product.product_condition)
        : { value: 1, label: "Satisfactory" },
    product_quality:
      product?.product_quality != null
        ? findConditionOption(product.product_quality)
        : { value: 1, label: "Satisfactory" },
    cfia_meat:
      product?.cfia_meat != null
        ? findYesNoOption(product.cfia_meat)
        : { value: 0, label: "N/A" },
    lab_results:
      product?.lab_results != null
        ? findYesNoOption(product.lab_results)
        : { value: 0, label: "N/A" },
    is_halal:
      product?.is_halal != null
        ? findYesNoOption(product.is_halal)
        : { value: 0, label: "N/A" },
  };

  const validate = () => true;

  const { values, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  const onSave = async () => {
    setSlideOver({ open: false, childComponent: null });
    const payload = {
      new_unit_cost: values.unit_cost,
      new_total_cost: values.total_cost,
      product_temperature: values.product_temperature || null,
      product_condition: values.product_condition?.value ?? null,
      product_quality: values.product_quality?.value ?? null,
      cfia_meat: values.cfia_meat?.value ?? null,
      lab_results: values.lab_results?.value ?? null,
      is_halal: values.is_halal?.value ?? null,
    };

    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message="Updating..." />,
    });
    try {
      const result = await makePostApiCall(
        `updateReceivingAssemblyItemGroup/${invoiceId}/${product.assembly_item_id}`,
        payload,
      );
      if (result.success && result.data) {
        setInvoiceItems(result.data.receiving_items);
        onSaveSuccess?.();
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update product.";
      showErrorModal(message);
    } finally {
      setEmptyModelOpen({ open: false, childComponent: null });
    }
  };

  const onCancel = () => {
    setSlideOver({ open: false, childComponent: null });
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex-1 overflow-auto p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Edit Product - {product?.product_name}
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit Cost
            </label>
            <InputNumber
              value={
                values.unit_cost != null ? parseFloat(values.unit_cost) : null
              }
              onValueChange={(e) =>
                handleInputChange({
                  target: { name: "unit_cost", value: e.value },
                })
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              className="w-full"
              inputClassName="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Cost
            </label>
            <InputNumber
              value={
                values.total_cost != null ? parseFloat(values.total_cost) : null
              }
              onValueChange={(e) =>
                handleInputChange({
                  target: { name: "total_cost", value: e.value },
                })
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              className="w-full"
              inputClassName="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-3">
            Product Quality Control
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <Input
                label="Product Temperature"
                name="product_temperature"
                onChange={handleInputChange}
                value={values.product_temperature}
                labelOn={true}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="product_condition"
                options={qualityConditionOptions}
                value={values.product_condition}
                title="Product Condition"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="product_quality"
                options={qualityConditionOptions}
                value={values.product_quality}
                title="Product Quality"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="cfia_meat"
                options={yesOrNoOptions}
                value={values.cfia_meat}
                title="CFIA Meat"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="lab_results"
                options={yesOrNoOptions}
                value={values.lab_results}
                title="Lab Results"
              />
            </div>
            <div>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="is_halal"
                options={yesOrNoOptions}
                value={values.is_halal}
                title="Is Halal"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
        >
          Save
        </button>
      </div>
    </div>
  );
}
